// English copy for the public "How on-demand FTP works" page (/on-demand-ftp). English is the source of truth:
// its shape is the OnDemandFtpCopy type that copy.es.ts and copy.pt.ts must match (copy.test.ts).
// Facts: photohawk-serverless docs/on-demand-ftp.md (design) and docs/help/on-demand-ftp.md (source text), and the
// "Your FTP server" panel (src/components/workspace/uploader/FtpServerPanel.tsx), whose labels are quoted here.
// Rules (owner, Oct 2026):
//  - The web uploader is the standard way to upload; FTP is only for when it's essential. The page opens with
//    "Before you start: the web uploader is faster", using only the approved claims: many photos at once, far
//    more than an FTP app like FileZilla (2 at a time by default, and no more than 10); it adapts to your
//    connection; previews go first, so the gallery is live in minutes; it resumes on its own.
//  - On-demand FTP (and previews first) are on every plan (owner, 19 Oct 2026): "every plan", never a plan name.
//  - "People in your workspace": never "photographer" or "organiser". No Wi-Fi cameras, cameras or tethering.
//  - "Originals", never "full-size". Shared FTP closes on 1 November 2026 (SHARED_FTP_RETIRES_ISO).
// Markup (rendered by the page): «Label» is a button or setting as the app shows it (bold; the uploader and
// FileZilla are in English, so translations keep these labels), `text` is an address, path or value (monospace).

const en = {
  seo: {
    title: "How on-demand FTP works | Photohawk",
    description:
      "If FTP is essential, everyone in your workspace can start their own Photohawk FTP server from the web uploader, on every plan. Ready in about 30 seconds, at a personal address that never changes, and it switches itself off after an hour without activity.",
  },
  hero: {
    badge: "Every plan",
    eyebrow: "Available now",
    title: "How on-demand FTP works",
    subtitle:
      "If FTP is essential, start your own FTP server from the web uploader whenever you need it. It's ready in about 30 seconds and switches itself off when you're done.",
  },
  before: {
    title: "Before you start: the web uploader is faster",
    body: "The web uploader is the standard way to upload, on every plan.",
    points: [
      { id: "parallel", text: "It sends many photos at once, far more than an FTP app like FileZilla (2 at a time by default, and no more than 10)." },
      { id: "adaptive", text: "It adapts to your connection, to make full use of it." },
      { id: "live", text: "Previews go first, so your gallery is live in minutes while the originals follow." },
      { id: "resume", text: "If your connection drops, it resumes on its own." },
    ],
    button: "Open the web uploader",
    link: "See the new web uploader",
  },
  what: {
    heading: "Your own FTP server, when you need one",
    body: "Everyone in your workspace can start their own FTP server from the web uploader, on every plan. Each person gets their own server, at a personal address that never changes, so you set up your FTP app once. Nothing runs when you're not using it: it switches itself off after an hour without activity.",
    /** Shown as a date badge. */
    date: "1 November 2026",
    closing: "Our always-on shared FTP servers close on 1 November 2026. Using them today? Start your own server and change the host in your FTP app.",
  },
  steps: {
    heading: "How it works",
    subtitle: "Four steps the first time. After that, start your server and upload.",
    items: [
      { id: "open", title: "Open the web uploader", body: "Go to `upload.photohawk.com` and sign in, or open a gallery's «Upload» tab." },
      { id: "start", title: "Start your server", body: "Click «FTP server» at the top, then «Start FTP server». It's ready in about 30 seconds." },
      { id: "connect", title: "Copy the address and login into your FTP app", body: "Your address, port 21, your Photohawk email and password. The address never changes." },
      { id: "upload", title: "Upload", body: "Add your photos to the gallery's folder. It switches off after an hour with nothing uploaded." },
    ],
    button: "Start your FTP server",
    buttonNote: "Opens the web uploader with your FTP server ready to start.",
  },
  panel: {
    heading: "What you'll see",
    body: "Everything is in the «Your FTP server» panel in the web uploader, with a copy button next to each detail.",
    points: [
      { id: "address", text: "Your address, like `swift-heron-4821.ftp.photohawk.com`. It's yours for good, so you only set it up once." },
      { id: "ip", text: "The server's IP address is shown too. It changes each time you start the server, so use the address when you can." },
      { id: "countdown", text: "A countdown to when it switches off, with «Keep it running» and «Stop»." },
    ],
    alt: "The “Your FTP server” panel showing Ready in Europe (Ireland), with the host swift-heron-4821.ftp.photohawk.com, the IP address, port 21, the username alex@example.com, passive mode and FTP over TLS, and a countdown with Keep it running and Stop buttons.",
  },
  details: {
    heading: "Your connection details",
    subtitle: "Enter these in your FTP app.",
    rows: [
      { id: "host", label: "Host", value: "Your address, e.g. `swift-heron-4821.ftp.photohawk.com` (or the IP address shown)" },
      { id: "port", label: "Port", value: "21" },
      { id: "user", label: "Username", value: "Your Photohawk email" },
      { id: "password", label: "Password", value: "Your Photohawk password, or your FTP password (below)" },
      { id: "mode", label: "Transfer mode", value: "Passive" },
      { id: "encryption", label: "Encryption", value: "Plain FTP, or FTPS (explicit FTP over TLS). For FTPS, use the host name, not the IP address." },
    ],
    switching: "Already used Photohawk FTP? Keep your username, password and folders. Just change the host to your new address.",
  },
  password: {
    heading: "An FTP-only password, if you'd like one",
    body: "Rather than saving your Photohawk password in an FTP app, click «Create FTP password» in the panel. It only works on your FTP server, with your email as the username. You can copy it, make a new one (the old one stops working within a minute) or remove it at any time. Your Photohawk password keeps working too.",
  },
  folders: {
    heading: "Your folders",
    body: "You'll see the same folders as before:",
    path: "/Galleries/<gallery>/<collection>",
    points: [
      { id: "single", text: "A gallery with one collection is simply `/Galleries/<gallery>`." },
      { id: "workspaces", text: "Upload to more than one workspace? Each one has its own folder, with its galleries inside." },
      { id: "create", text: "Create the gallery and its collections in Photohawk first. Your photos appear in the gallery within moments, just as with the web uploader." },
    ],
  },
  off: {
    heading: "It switches itself off",
    body: "Your server switches off after 1 hour without activity, so there's nothing to remember. The panel shows a countdown.",
    points: [
      { id: "keep", text: "«Keep it running» restarts the countdown without uploading anything." },
      { id: "stop", text: "«Stop» switches it off straight away when you've finished." },
      { id: "caps", text: "To be safe, it also switches off after 12 hours once nothing is uploading, and after 24 hours at most." },
      { id: "again", text: "Start it again whenever you like, with the same address and login." },
    ],
  },
  regions: {
    heading: "Regions",
    body: "Run your server in Europe (Ireland), US East (N. Virginia) or Asia-Pacific (Malaysia). The nearest one is picked for you, and you can change it before you start.",
  },
  filezilla: {
    heading: "Example: setting up FileZilla",
    subtitle: "Other FTP apps use the same settings, sometimes under slightly different names.",
    steps: [
      { id: "site", text: "Open «File → Site Manager» and click «New site»." },
      { id: "protocol", text: "Protocol: «FTP – File Transfer Protocol»." },
      { id: "host", text: "Host: your address, e.g. `swift-heron-4821.ftp.photohawk.com`. Port: `21`." },
      { id: "tls", text: "Encryption: «Use explicit FTP over TLS if available»." },
      { id: "login", text: "Logon Type: «Normal». User: your Photohawk email. Password: your FTP password or Photohawk password." },
      { id: "passive", text: "On the «Transfer Settings» tab, choose «Passive». Click «Connect», then open `Galleries`." },
    ],
    tip: "Sending a big folder? FileZilla sends 2 at a time by default, and no more than 10. You can raise its simultaneous transfers setting up to 10 («Settings → Transfers»), or use the web uploader, which sends many more at once.",
  },
  trouble: {
    heading: "If something doesn't work",
    items: [
      { id: "connect", title: "Can't connect", body: "Check the panel says «Ready». If the host name was only just created, use the IP address for a minute." },
      { id: "listing", title: "Connected, but the folder list hangs", body: "Turn on passive mode." },
      { id: "logins", title: "“Too many failed logins”", body: "Wait 15 minutes, then check the username (your email) and password." },
    ],
  },
  faq: {
    heading: "Questions",
    items: [
      {
        q: "Why did my server switch off?",
        a: "It switches off after an hour without activity, so it never runs when you're not using it. Start it again any time: the address and login stay the same. To keep it going without uploading, click «Keep it running». It also switches off after 12 hours once nothing is uploading, and after 24 hours at most.",
      },
      {
        q: "Can two people run servers at once?",
        a: "Yes. Everyone in your workspace can start their own server, one each, at their own address. Only you can log in to yours.",
      },
      {
        q: "Is it secure?",
        a: "Yes. With FTPS (FTP over TLS) on your host name, the connection is encrypted with a trusted certificate. Only your login works on your server, and after repeated failed logins the server locks that connection out for 15 minutes.",
      },
      {
        q: "Do I pay extra?",
        a: "No. Your own FTP server is included in every plan.",
      },
      {
        q: "Is it on every plan?",
        a: "Yes, Lite included. For everyday uploads, the web uploader or Photohawk Studio is faster; your own FTP server is there when FTP is essential.",
      },
      {
        q: "What happens to the old shared FTP?",
        a: "The always-on shared FTP servers close on 1 November 2026, and work as before until then. To keep using FTP, start your own server and change the host in your FTP app to your new address. Your username, password and folders stay the same.",
      },
    ],
  },
  cta: {
    heading: "Ready when you are",
    body: "For everyday uploads, use the web uploader. When FTP is essential, your own server is a click away.",
    primary: "Open the web uploader",
    secondary: "Start your FTP server",
  },
};

export type OnDemandFtpCopy = typeof en;
export default en;
